import React, { useEffect, useState } from "react";
import { useAuth } from "@/AuthContext";
import { PushPin, PushPinSlash, X, MagnifyingGlass, Stack, ArrowUpRight, TreeStructure } from "@phosphor-icons/react";
import { useStructureDetail } from "@/hooks/useStructureDetail";
import StructureTree from "./StructureTree";

const StructureChips = ({ structures, previewSid, onPick }) => (
  <div className="flex flex-wrap gap-2" data-testid="structure-chips">
    {structures.map(s => {
      const on = s.id === previewSid;
      return (
        <button key={s.id} onClick={() => onPick(s.id)} data-testid={`structure-chip-${s.id}`}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors duration-150 ${on ? "bg-blue-600 border-blue-600 text-white shadow-sm" : "bg-neutral-50 border-neutral-200 text-neutral-500 hover:text-neutral-800 hover:border-neutral-300"}`}>
          <TreeStructure size={13} weight={on ? "fill" : "regular"}/> {s.name}
        </button>
      );
    })}
  </div>
);

const WholeStructureCard = ({ active, onClick }) => (
  <button onClick={onClick} data-testid="select-whole-structure"
    className={`w-full text-left flex items-start gap-3 px-4 py-3.5 rounded-xl border transition-colors duration-150 bg-blue-50 ${active ? "border-blue-400 ring-2 ring-blue-100" : "border-blue-100 hover:border-blue-300"}`}>
    <Stack size={20} weight="duotone" className="text-blue-600 mt-0.5 shrink-0"/>
    <span className="flex-1">
      <span className="block font-bold text-blue-700">Toda la estructura</span>
      <span className="block text-xs text-neutral-500 mt-0.5">Acumula toda tu estructura autorizada</span>
    </span>
    <ArrowUpRight size={16} weight="bold" className="text-blue-600 mt-1"/>
  </button>
);

export default function StructurePanel({ orgName, pinned, onTogglePin, onClose }) {
  const { structures, activeStructureId, activeNodeId, setActiveStructureNode } = useAuth();
  const [previewSid, setPreviewSid] = useState(activeStructureId || structures?.[0]?.id || null);
  const [query, setQuery] = useState("");
  const detail = useStructureDetail(previewSid);

  useEffect(() => {
    if (pinned) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pinned, onClose]);

  const select = (nodeId) => { setActiveStructureNode(previewSid, nodeId); if (!pinned) onClose(); };
  const isActiveStructure = previewSid === activeStructureId;

  return (
    <div className="h-full flex flex-col bg-white" data-testid="structure-navigator-menu">
      <div className="px-5 pt-5 pb-4 border-b border-neutral-200 space-y-4">
        <div className="flex items-center gap-3">
          <span className="px-2.5 py-1 rounded-md bg-blue-600 text-white text-[11px] font-bold tracking-widest">ESTRUCTURA</span>
          <h2 className="flex-1 min-w-0 line-clamp-2 break-words leading-tight font-display text-lg font-bold text-neutral-900" data-testid="structure-panel-org">{orgName}</h2>
          <button onClick={onTogglePin} title={pinned ? "Desfijar panel" : "Fijar panel"} data-testid="structure-panel-pin-btn"
            className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${pinned ? "bg-blue-50 border-blue-300 text-blue-700" : "border-neutral-200 text-neutral-500 hover:text-neutral-900"}`}>
            {pinned ? <PushPinSlash size={16}/> : <PushPin size={16}/>}
          </button>
          <button onClick={onClose} title="Cerrar" data-testid="structure-panel-close-btn"
            className="w-9 h-9 rounded-full border border-neutral-200 text-neutral-500 hover:text-neutral-900 flex items-center justify-center">
            <X size={16}/>
          </button>
        </div>
        <p className="text-xs text-neutral-500 -mt-1">Elige la estructura y navega el árbol organizacional.</p>
        <StructureChips structures={structures || []} previewSid={previewSid} onPick={(id) => { setPreviewSid(id); setQuery(""); }}/>
        <div className="relative">
          <MagnifyingGlass size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400"/>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar nodo, empresa o código..."
            className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-neutral-200 bg-white text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
            data-testid="structure-search-input"/>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-3">
        {!detail ? <p className="px-5 py-6 text-sm text-neutral-400">Cargando…</p> : (
          <>
            <div className="px-3 pb-2">
              <WholeStructureCard active={isActiveStructure && !activeNodeId} onClick={() => select(null)}/>
            </div>
            <StructureTree detail={detail} query={query} activeNodeId={activeNodeId} isActiveStructure={isActiveStructure} onSelect={select}/>
          </>
        )}
      </div>

      {activeStructureId && (
        <div className="px-5 py-3 border-t border-neutral-200 flex justify-between items-center text-xs">
          <span className="text-neutral-500">El nodo elegido filtra todos los módulos.</span>
          <button onClick={() => { setActiveStructureNode(null, null); if (!pinned) onClose(); }} className="font-semibold text-blue-600 hover:underline" data-testid="navigator-clear-btn">
            Quitar estructura
          </button>
        </div>
      )}
    </div>
  );
}
